import { useCallback, useMemo, useState } from 'react';

export interface CursorPaginationState {
  page: number;
  pageSize: number;
  cursor: {
    direction: 'after' | 'before' | null;
    value?: string;
  };
}

export interface CursorPaginationOptions {
  pageSizeOptions?: number[];
  initialPageSize?: number;
  initialPage?: number;
  initialCursor?: CursorPaginationState['cursor'];
}

const DEFAULT_PAGE_SIZE = 10;

type CursorMeta =
  | {
      next?: string | null;
      prev?: string | null;
      exactTotal?: number | null;
    }
  | null
  | undefined;

type NormalizedMeta = {
  next?: string;
  prev?: string;
  exactTotal: number | null;
};

const parseLinkHeader = (header?: string | null): { next?: string; prev?: string } => {
  if (!header) {
    return {};
  }
  return header.split(',').reduce<{ next?: string; prev?: string }>((acc, entry) => {
    const match = entry.match(/<([^>]+)>;\s*rel="([^"]+)"/i);
    if (!match) {
      return acc;
    }
    const [, url, rel] = match;
    try {
      const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      const after = parsed.searchParams.get('after');
      const before = parsed.searchParams.get('before');
      if (rel === 'next' && after) {
        acc.next = after;
      }
      if (rel === 'prev' && before) {
        acc.prev = before;
      }
    } catch {
      // Ignore parse errors
    }
    return acc;
  }, {});
};

export const useCursorPagination = (options?: CursorPaginationOptions) => {
  const pageSizeOptions = options?.pageSizeOptions;
  const initialPageSize = options?.initialPageSize ?? pageSizeOptions?.[0] ?? DEFAULT_PAGE_SIZE;
  const initialPage = options?.initialPage && options.initialPage > 1 ? options.initialPage : 1;
  const initialCursor = options?.initialCursor;
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [afterTokens, setAfterTokens] = useState<Record<number, string | undefined>>(
    () => initialCursor?.direction === 'after' && initialCursor.value ? { [initialPage]: initialCursor.value } : {}
  );
  const [beforeTokens, setBeforeTokens] = useState<Record<number, string | undefined>>(
    () => initialCursor?.direction === 'before' && initialCursor.value ? { [initialPage]: initialCursor.value } : {}
  );
  const [pageDirections, setPageDirections] = useState<Record<number, 'after' | 'before' | null>>(
    () => ({ 1: null, [initialPage]: initialCursor?.direction ?? null })
  );
  const [lastMeta, setLastMeta] = useState<NormalizedMeta | null>(null);

  const currentDirection = pageDirections[page] ?? null;
  const afterValueForPage = afterTokens[page];
  const beforeValueForPage = beforeTokens[page];
  const currentCursor = useMemo(() => {
    const value =
      currentDirection === 'after'
        ? afterValueForPage
        : currentDirection === 'before'
          ? beforeValueForPage
          : undefined;
    return { direction: currentDirection, value };
  }, [currentDirection, afterValueForPage, beforeValueForPage, page]);

  const reset = useCallback(() => {
    setPage(1);
    setAfterTokens({});
    setBeforeTokens({});
    setPageDirections({ 1: null });
    setLastMeta(null);
  }, []);

  const restore = useCallback((state: CursorPaginationState) => {
    setPage(state.page);
    setPageSize(state.pageSize);
    setAfterTokens(state.cursor.direction === 'after' && state.cursor.value ? { [state.page]: state.cursor.value } : {});
    setBeforeTokens(state.cursor.direction === 'before' && state.cursor.value ? { [state.page]: state.cursor.value } : {});
    setPageDirections({ 1: null, [state.page]: state.cursor.direction });
    setLastMeta(null);
  }, []);

  const cursorForPage = useCallback((targetPage: number): CursorPaginationState['cursor'] => {
    if (targetPage === 1) return { direction: null };
    const direction = pageDirections[targetPage] ?? null;
    const value = direction === 'after' ? afterTokens[targetPage] : direction === 'before' ? beforeTokens[targetPage] : undefined;
    return { direction, value };
  }, [afterTokens, beforeTokens, pageDirections]);

  const handlePageSizeChange = useCallback(
    (nextSize: number) => {
      setPageSize(nextSize);
      reset();
    },
    [reset]
  );

  const registerPageMeta = useCallback(
    (meta: CursorMeta, linkHeader?: string | null) => {
      const links = parseLinkHeader(linkHeader);
      const normalized: NormalizedMeta = {
        next: links.next ?? (meta?.next ?? undefined) ?? undefined,
        prev: links.prev ?? (meta?.prev ?? undefined) ?? undefined,
        exactTotal: typeof meta?.exactTotal === 'number' && !Number.isNaN(meta.exactTotal) ? meta.exactTotal : null
      };
      setLastMeta(normalized);

      setAfterTokens((prev) => {
        if (normalized.next === undefined) {
          if (prev[page + 1] === undefined) {
            return prev;
          }
          const { [page + 1]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [page + 1]: normalized.next || undefined };
      });
      setBeforeTokens((prev) => {
        if (normalized.prev === undefined) {
          if (prev[page - 1] === undefined) {
            return prev;
          }
          const { [page - 1]: _omit, ...rest } = prev;
          return rest;
        }
        return { ...prev, [page - 1]: normalized.prev || undefined };
      });
      setPageDirections((prev) => {
        const nextDirections = { ...prev };
        if (normalized.next !== undefined) {
          nextDirections[page + 1] = 'after';
        }
        if (normalized.prev !== undefined && page > 2) {
          nextDirections[page - 1] = 'before';
        }
        if (!(page in nextDirections)) {
          nextDirections[page] = nextDirections[page] ?? null;
        }
        return nextDirections;
      });
    },
    [page]
  );

  const adjustExactTotal = useCallback((delta: number) => {
    if (!Number.isFinite(delta) || delta === 0) {
      return;
    }
    setLastMeta((previous) => {
      if (!previous || previous.exactTotal == null) {
        return previous;
      }
      return { ...previous, exactTotal: Math.max(0, previous.exactTotal + delta) };
    });
  }, []);

  const deriveMeta = useCallback(
    (rowsLength: number) => {
      const exactTotal = lastMeta?.exactTotal ?? null;
      const hasNextPage = exactTotal != null ? page * pageSize < exactTotal : Boolean(lastMeta?.next);
      const hasPrevPage = Boolean(lastMeta?.prev) || page > 1;
      const fallbackTotal = hasNextPage ? page * pageSize + 1 : (page - 1) * pageSize + rowsLength;
      const totalCount = Math.max(exactTotal ?? fallbackTotal, (page - 1) * pageSize + rowsLength);
      const pageCount =
        exactTotal != null ? Math.max(1, Math.ceil(exactTotal / pageSize)) : hasNextPage ? page + 1 : page;
      return { hasNextPage, hasPrevPage, exactTotal, totalCount, pageCount };
    },
    [lastMeta, page, pageSize]
  );

  return {
    state: { page, pageSize, cursor: currentCursor },
    setPage,
    setPageSize: handlePageSizeChange,
    reset,
    restore,
    cursorForPage,
    registerPageMeta,
    adjustExactTotal,
    deriveMeta
  };
};
